import { ReactNode } from 'react';
import { ShieldAlert } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';

export const CampaignAccessDenied = ({ code }: { code: string }) => (
  <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-gray-100 bg-white py-20 text-center shadow-sm">
    <div className="flex h-14 w-14 items-center justify-center rounded-full bg-red-50">
      <ShieldAlert className="h-7 w-7 text-red-500" />
    </div>
    <h2 className="text-lg font-semibold text-gray-800">Access restricted</h2>
    <p className="max-w-sm text-sm text-gray-500">
      This action requires the{' '}
      <code className="rounded bg-gray-100 px-1.5 py-0.5 text-xs">{code}</code> permission.
    </p>
  </div>
);

export const CampaignPageShell = ({
  icon,
  title,
  description,
  children,
  loading,
}: {
  icon: ReactNode;
  title: string;
  description: string;
  children: ReactNode;
  loading?: boolean;
}) => {
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="mx-auto max-w-screen-2xl space-y-4">
          <Skeleton className="h-10 w-48" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div>
          <div className="mb-1 flex items-center gap-2.5">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
              {icon}
            </div>
            <h1 className="text-2xl font-black text-gray-900">{title}</h1>
          </div>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
        {children}
      </div>
    </div>
  );
};

export const CampaignTabs = <T extends string>({
  tabs,
  active,
  onChange,
}: {
  tabs: { id: T; label: string }[];
  active: T;
  onChange: (id: T) => void;
}) => (
  <div className="flex flex-wrap gap-2">
    {tabs.map((tab) => (
      <button
        key={tab.id}
        type="button"
        onClick={() => onChange(tab.id)}
        className={`rounded-md border px-3 py-1.5 text-sm ${
          active === tab.id
            ? 'border-blue-200 bg-blue-50 text-blue-800'
            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
        }`}
      >
        {tab.label}
      </button>
    ))}
  </div>
);
